import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HardHat, Lightning, MapTrifold, Drop, Buildings, ChartLineUp, ArrowRight, X } from '@phosphor-icons/react';
import { useLanguage } from '../context/LanguageContext';

const fadeIn = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.6, delay },
  viewport: { once: true },
});

const ServiceModal = ({ service, icon: Icon, onClose, t }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 z-[100] flex items-center justify-center p-4"
    onClick={onClose}
  >
    <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
    <motion.div
      initial={{ opacity: 0, y: 30, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 30, scale: 0.95 }}
      className="relative z-10 bg-white max-w-2xl w-full max-h-[85vh] overflow-y-auto"
      onClick={(e) => e.stopPropagation()}
    >
      <button onClick={onClose} className="absolute top-4 right-4 w-10 h-10 bg-black/10 hover:bg-[#FF6B00] flex items-center justify-center transition-colors">
        <X size={20} className="text-[#0B1320] hover:text-white" weight="bold" />
      </button>

      <div className="bg-[#0B1320] p-8">
        <Icon size={40} className="text-[#FF6B00] mb-4" weight="bold" />
        <h3 className="text-2xl font-bold text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{service.title}</h3>
      </div>

      <div className="p-8 space-y-6">
        <p className="text-[#475569] leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>{service.desc}</p>

        <div>
          <h4 className="text-sm font-medium text-[#0B1320] uppercase tracking-wider mb-3">Livrables</h4>
          <ul className="space-y-2">
            {service.items.map((item, i) => (
              <li key={i} className="flex items-center gap-2 text-sm text-[#475569]">
                <span className="w-1.5 h-1.5 bg-[#FF6B00] rounded-full" />
                {item}
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-[#F8FAFC] border border-[#E2E8F0] p-4">
          <h4 className="text-xs font-medium text-[#0B1320] uppercase tracking-wider mb-2">Standards</h4>
          <p className="text-sm text-[#FF6B00] font-mono">{service.standards}</p>
        </div>

        <div>
          <h4 className="text-sm font-medium text-[#0B1320] uppercase tracking-wider mb-3">Processus</h4>
          <p className="text-sm text-[#475569] leading-relaxed">{service.process}</p>
        </div>
      </div>
    </motion.div>
  </motion.div>
);

export const ServicesSection = () => {
  const { t } = useLanguage();
  const [selectedIndex, setSelectedIndex] = useState(null);

  const services = [
    { key: 'civil', icon: HardHat },
    { key: 'electrical', icon: Lightning },
    { key: 'topography', icon: MapTrifold },
    { key: 'drilling', icon: Drop },
    { key: 'construction', icon: Buildings },
    { key: 'consulting', icon: ChartLineUp },
  ];

  return (
    <section id="services" className="py-24 lg:py-32 bg-[#F8FAFC]" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div {...fadeIn()} className="text-center mb-6">
          <p className="text-[#FF6B00] text-xs sm:text-sm font-medium uppercase tracking-[0.2em] mb-4">{t.services.title}</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0B1320] tracking-tight max-w-3xl mx-auto" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }} data-testid="services-title">
            {t.services.subtitle}
          </h2>
        </motion.div>
        <motion.p {...fadeIn(0.1)} className="text-center text-[#475569] max-w-2xl mx-auto mb-16" style={{ fontFamily: "'Manrope', sans-serif" }}>
          {t.services.intro}
        </motion.p>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s, index) => {
            const service = t.services[s.key];
            const Icon = s.icon;
            return (
              <motion.div
                key={s.key}
                {...fadeIn(index * 0.08)}
                className="group bg-white border border-[#E2E8F0] hover:border-[#FF6B00]/30 p-8 transition-all duration-300 cursor-pointer relative overflow-hidden"
                onClick={() => setSelectedIndex(index)}
                data-testid={`service-card-${index}`}
              >
                <div className="absolute top-0 left-0 w-full h-1 bg-[#FF6B00] transform -translate-x-full group-hover:translate-x-0 transition-transform duration-500" />

                <div className="w-14 h-14 bg-[#0B1320] flex items-center justify-center mb-6 group-hover:bg-[#FF6B00] transition-colors duration-300">
                  <Icon size={28} className="text-white" weight="bold" />
                </div>

                <h3 className="text-xl font-semibold text-[#0B1320] mb-3 tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  {service.title}
                </h3>

                <p className="text-sm text-[#475569] mb-6 leading-relaxed line-clamp-3" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  {service.desc}
                </p>

                <ul className="space-y-2 mb-6">
                  {service.items.slice(0, 3).map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-[#0B1320]">
                      <span className="w-1.5 h-1.5 bg-[#FF6B00] rounded-full" />
                      {item}
                    </li>
                  ))}
                  {service.items.length > 3 && (
                    <li className="text-xs text-[#94A3B8]">+{service.items.length - 3} autres</li>
                  )}
                </ul>

                <div className="flex items-center justify-between">
                  <span className="text-xs text-[#FF6B00] font-mono">{service.standards}</span>
                  <ArrowRight size={16} className="text-[#0B1320] group-hover:text-[#FF6B00] transition-colors" weight="bold" />
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Detail Modal */}
      <AnimatePresence>
        {selectedIndex !== null && (
          <ServiceModal
            service={t.services[services[selectedIndex].key]}
            icon={services[selectedIndex].icon}
            onClose={() => setSelectedIndex(null)}
            t={t}
          />
        )}
      </AnimatePresence>
    </section>
  );
};
